<script setup lang="ts">
/**
 * 监管端根组件(S-11)。
 *
 * 这里只有一个判断:有没有令牌。有 → 套 AppShell(壳,徽标进侧栏、无全局顶栏);
 * 没有 → 只放路由出口,登录页自带 LoginLayout(与运营端登录页同款,S-48 的 G4)——
 * G4 的整页文案住在 views/LoginView.vue 里,这一层不再传任何登录页的字。
 * 骨架、主题、消息出口都在 @sea-drone/ui 里,这个文件不再自己拼导航条 ——
 * 两个端各拼一份导航,迟早一份两样而没人发现。
 *
 * ═══ 两块大屏是全屏脱壳页(S-49 的 /dashboard、S-50 的 /monitor)═══
 *
 * 按 v2 · G1(1920×1080 投屏主秀),/dashboard 不套壳:画板把它画成整块大屏,
 * 没有侧栏、内容区不留白 —— DashboardView 自己画顶栏、主区与底栏,自己挂深色
 * 令牌变量(useToneVars)。**大屏的导航件是人要求加的补件(2026-10-18)**:
 * 画板没画导航,而这一页是脱壳的、吃不到壳里那份 NAV,于是「进得去出不来」——
 * 人原话「dashboard 页面需要有菜单,不然点不到其他页面去」。DashboardView 顶栏
 * 因此自己画了一条四项页签(不加侧栏:大屏是定版布局,侧栏会把中央列挤走)。
 * S-50 的 /monitor(v2 · G5 综合监控)是同一种做法壳的第三块:画板把它画成
 * 整块管理台,页签与侧栏导航都画在页面里 —— MonitorView 自己画、自己挂变量。
 * 除这三条与 /login 之外的页面照旧套壳。
 *
 * 导航四条(Y-32 的监管端清单 + S-53 新增的起飞申请):数据大屏、订单、起飞申请、综合监控。
 * 视频直播那块整块不在本期(L0-06 搁置、M-09 排除),所以这里不会有第五条入口:
 * 导航里出现一个点不开的东西,演示时就是一句解释不清的话。
 *
 * ═══ 这份清单是「监管端有哪些页面」的唯一定义 ═══
 *
 * 两个脱壳页(/dashboard、/monitor)吃不到它,各自在页面里手写了一份 —— 于是
 * 同一份事实住了三处。S-53 加「起飞申请」时只改了这里,监控页那份静默停在三项,
 * 页面上看不出任何异常,只是少一个入口(守卫也管不到:check-admin-nav 的三向核对
 * 只看得到这一份)。2026-10-18 补上:check-admin-nav 新增第 ④ 组判据,页面里手写的
 * 清单必须与这份 NAV 逐项对上(label / to / 顺序),漂移就报红。
 *
 * 令牌存在 sea-drone.gov.token 这个键上,与运营端互不挤占 ——
 * 共用一个键会让后登的那个把先登的挤掉,表现是「运营端莫名其妙掉线」。
 */
import { computed, ref } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import { AppShell, type NavItem } from '@sea-drone/ui'
import { clearToken, hasToken } from './api/client'

/**
 * 四条导航项。顺序即展示顺序,与路由前缀一致(MonitorView 页面内的页签/侧栏同名同序)。
 * 起飞申请(S-53)是第四项:它是监管端这一轮唯一新增的页面,也是唯一一处
 * 「监管端要动手」的地方 —— 所以它排在自己的那组里(订单之后、监控之前)。
 */
const NAV: readonly NavItem[] = [
  { key: 'dashboard', label: '数据大屏', to: '/dashboard', icon: 'dashboard' },
  { key: 'orders', label: '订单', to: '/orders', icon: 'list' },
  { key: 'takeoff-applications', label: '起飞申请', to: '/takeoff-applications', icon: 'clipboard' },
  { key: 'monitor', label: '综合监控', to: '/monitor', icon: 'monitor' },
]

/** 不套壳的路由:登录页自带 LoginLayout;两块大屏自己画整屏(S-49 / S-50) */
const SHELL_LESS = new Set(['/login', '/dashboard', '/monitor'])

const router = useRouter()
const route = useRoute()
const loggedIn = ref(hasToken())

// 路由一变就重新看一眼令牌 —— 登录/登出之后骨架要跟着换
router.afterEach(() => {
  loggedIn.value = hasToken()
})

const showShell = computed(() => loggedIn.value && !SHELL_LESS.has(route.path))

function logout(): void {
  clearToken()
  loggedIn.value = false
  void router.push('/login')
}
</script>

<template>
  <AppShell v-if="showShell" tone="dark" product="监管端" badge="全程只读" :nav="NAV" @logout="logout">
    <router-view />
  </AppShell>
  <!-- 登录页自带 LoginLayout(它承载 G4 的整页文案):这里只放路由出口 -->
  <router-view v-else />
</template>
